import React, { useCallback, useEffect, useRef, useState } from 'react';

// VpDebugChip (2026-10-19): on-device viewport diagnostic chip for the iOS
// standalone PWA black-bar bug (diagnosis CLOSED: desktop fix 1305f13, mobile
// fix 19a24f6). OPT-IN ONLY since 2026-10-19 — the chip is OFF by default so
// screen recordings stay clean. It renders ONLY when BOTH hold:
//   (a) display-mode: standalone, AND
//   (b) the page URL carries the opt-in flag — query `?vpdebug=1` or hash
//       `#vpdebug=1` — which sets sessionStorage.vpdebug='1' for the rest of
//       the session (SPA navigation keeps it; a fresh load without the param
//       clears it).
// RE-ENABLE: add ?vpdebug=1 to the URL once per session, e.g.
//   https://<host>/?vpdebug=1   (or #vpdebug=1 on a hash-routed entry point)
// Mounted once at the app root (main.jsx, sibling of <App />).

// Opt-in gate (2026-10-19): see header comment. Evaluated once at module load.
// A fresh load WITHOUT the param clears any stale sessionStorage flag, so the
// chip cannot leak into later sessions; SPA navigation within a session keeps
// the flag (and the mounted chip) alive.
function resolveVpDebugEnabled(): boolean {
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('vpdebug') === '1';
    const fromHash = /(^|[?&#])vpdebug=1(&|$)/.test(window.location.hash);
    if (fromQuery || fromHash) {
      sessionStorage.setItem('vpdebug', '1');
      return true;
    }
    sessionStorage.removeItem('vpdebug');
    return false;
  } catch {
    return false;
  }
}

const VP_DEBUG_ENABLED = resolveVpDebugEnabled();

type VpPoint = {
  target: string;
  bg: string;
};

// Published by the FIX A inline script in index.html (guarded jiggle).
type VvhDebug = {
  h0: number;
  h1: number;
  pass: number;
  published: number;
};

type VpSnapshot = {
  at: number;
  innerHeight: number;
  innerWidth: number;
  visualViewport: {
    height: number | null;
    offsetTop: number | null;
    offsetLeft: number | null;
    pageTop: number | null;
    scale: number | null;
  } | null;
  documentElement: {
    clientHeight: number;
    clientWidth: number;
    scrollHeight: number;
    computedHeight: string;
    computedMinHeight: string;
  } | null;
  body: {
    top: number;
    bottom: number;
    height: number;
    computedHeight: string;
    computedMinHeight: string;
  } | null;
  root: {
    top: number;
    bottom: number;
    height: number;
  } | null;
  safeAreaInsetBottom: number;
  safeAreaInsetTop: number;
  htmlBg: string;
  stripProbe: VpPoint | null;
  vvhDebug: VvhDebug | null;
  resizeEvents: { windowResize: number; visualViewportResize: number };
  appHeader: { top: number; bottom: number; height: number; computedHeight: string } | null;
  appNav: { top: number; bottom: number; height: number; computedHeight: string } | null;
  appShellBottom: { top: number; bottom: number; height: number; computedHeight: string } | null;
  probes: {
    atCenterBottom: VpPoint | null;
    aboveSafeArea: VpPoint | null;
    atTop: VpPoint | null;
  };
  scroll: {
    windowScrollY: number;
    scroller: string | null;
    scrollerScrollTop: number | null;
    scrollerClientHeight: number | null;
    scrollerScrollHeight: number | null;
  };
  displayModeStandalone: boolean;
  navigatorOnLine: boolean;
};

function describePoint(el: Element | null): VpPoint | null {
  if (!el) return null;
  const cls = typeof el.className === 'string' && el.className ? el.className.split(/\s+/).slice(0, 3).join('.') : '';
  let bg = '';
  try {
    bg = getComputedStyle(el).backgroundColor;
  } catch {
    bg = 'n/a';
  }
  return { target: el.tagName.toLowerCase() + (cls ? '.' + cls : ''), bg };
}

function rectOf(el: Element | null) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: Math.round(r.top * 10) / 10, bottom: Math.round(r.bottom * 10) / 10, height: Math.round(r.height * 10) / 10 };
}

// Find the element that actually scrolls: check the document scroller first,
// then the nearest overflow-auto/scroll container inside #root.
function findScroller(): { el: Element | null; kind: string } {
  const se = document.scrollingElement;
  if (se && se.scrollHeight > se.clientHeight + 1) {
    return { el: se, kind: se === document.documentElement ? 'documentElement' : 'body' };
  }
  const root = document.getElementById('root');
  if (root) {
    const all = Array.from(root.querySelectorAll('*')).slice(0, 800);
    for (let i = 0; i < all.length; i++) {
      const el = all[i];
      const cs = getComputedStyle(el);
      if ((cs.overflowY === 'auto' || cs.overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1) {
        const cls = typeof el.className === 'string' && el.className ? '.' + el.className.split(/\s+/).slice(0, 2).join('.') : '';
        return { el, kind: 'inner#' + i + cls };
      }
    }
  }
  return { el: se || document.documentElement, kind: se === document.body ? 'body(no-overflow)' : 'documentElement(no-overflow)' };
}

function collectSnapshot(safeProbeHeight: number): VpSnapshot {
  const vv = window.visualViewport;
  const de = document.documentElement;
  const body = document.body;
  const root = document.getElementById('root');
  const centerX = window.innerWidth / 2;
  const scroller = findScroller();

  const cs = (el: Element | null, prop: string) => {
    try {
      return el ? (getComputedStyle(el) as unknown as Record<string, string>)[prop] : 'n/a';
    } catch {
      return 'n/a';
    }
  };

  return {
    at: Date.now(),
    innerHeight: window.innerHeight,
    innerWidth: window.innerWidth,
    visualViewport: vv
      ? {
          height: Math.round(vv.height * 10) / 10,
          offsetTop: vv.offsetTop,
          offsetLeft: vv.offsetLeft,
          pageTop: vv.pageTop,
          scale: vv.scale,
        }
      : null,
    documentElement: de
      ? {
          clientHeight: de.clientHeight,
          clientWidth: de.clientWidth,
          scrollHeight: de.scrollHeight,
          computedHeight: cs(de, 'height'),
          computedMinHeight: cs(de, 'minHeight'),
        }
      : null,
    body: body
      ? {
          top: Math.round(body.getBoundingClientRect().top * 10) / 10,
          bottom: Math.round(body.getBoundingClientRect().bottom * 10) / 10,
          height: Math.round(body.getBoundingClientRect().height * 10) / 10,
          computedHeight: cs(body, 'height'),
          computedMinHeight: cs(body, 'minHeight'),
        }
      : null,
    root: rectOf(root),
    safeAreaInsetBottom: safeProbeHeight,
    safeAreaInsetTop: safeProbeTop ? safeProbeTop.offsetHeight : 0,
    htmlBg: (() => {
      try {
        return getComputedStyle(document.documentElement).backgroundColor;
      } catch {
        return 'n/a';
      }
    })(),
    stripProbe: describePoint(document.elementFromPoint(centerX, window.innerHeight + 30)),
    vvhDebug: (window as unknown as { __vvhDebug?: VvhDebug }).__vvhDebug ?? null,
    resizeEvents: { windowResize: resizeCounts.windowResize, visualViewportResize: resizeCounts.visualViewportResize },
    appHeader: (() => {
      const el = document.querySelector('.app-header');
      const r = rectOf(el);
      return r ? { ...r, computedHeight: cs(el, 'height') } : null;
    })(),
    appNav: (() => {
      const el = document.querySelector('.app-nav');
      const r = rectOf(el);
      return r ? { ...r, computedHeight: cs(el, 'height') } : null;
    })(),
    appShellBottom: (() => {
      const el = document.querySelector('.app-shell-bottom');
      const r = rectOf(el);
      return r ? { ...r, computedHeight: cs(el, 'height') } : null;
    })(),
    probes: {
      atCenterBottom: describePoint(document.elementFromPoint(centerX, window.innerHeight - 2)),
      aboveSafeArea: describePoint(document.elementFromPoint(centerX, Math.max(0, window.innerHeight - safeProbeHeight - 2))),
      atTop: describePoint(document.elementFromPoint(centerX, 2)),
    },
    scroll: {
      windowScrollY: window.scrollY,
      scroller: scroller.kind,
      scrollerScrollTop: scroller.el ? Math.round(scroller.el.scrollTop) : null,
      scrollerClientHeight: scroller.el ? scroller.el.clientHeight : null,
      scrollerScrollHeight: scroller.el ? scroller.el.scrollHeight : null,
    },
    displayModeStandalone: window.matchMedia('(display-mode: standalone)').matches,
    navigatorOnLine: navigator.onLine,
  };
}

// Safe-area probe: a hidden div whose height resolves env(safe-area-inset-bottom).
// Created at module scope so the resolved inset is measurable from the very
// first snapshot (and re-measured on every NOW refresh). GATED (2026-10-19):
// probes are only created when the chip is enabled — a gated-off session has
// zero DOM footprint and no extra listeners.
function makeSafeProbe(insetExpr: string): HTMLDivElement | null {
  if (!VP_DEBUG_ENABLED) return null;
  const el = document.createElement('div');
  el.setAttribute('aria-hidden', 'true');
  el.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;height:' + insetExpr + ';width:0;top:0;left:0;';
  if (document.body) {
    document.body.appendChild(el);
  } else {
    document.addEventListener('DOMContentLoaded', () => document.body.appendChild(el));
  }
  return el;
}
const safeProbe = makeSafeProbe('env(safe-area-inset-bottom)');

// Same probe for the TOP inset (tests the 62px = status-bar-inset hypothesis).
const safeProbeTop = makeSafeProbe('env(safe-area-inset-top)');

// Resize/visualViewport-resize counters (2026-10-19): count from module load so
// the dump records whether ANY geometry event fired between load and now.
const resizeCounts = { windowResize: 0, visualViewportResize: 0 };
if (VP_DEBUG_ENABLED) {
  window.addEventListener('resize', () => { resizeCounts.windowResize++; }, { passive: true });
  try {
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => { resizeCounts.visualViewportResize++; }, { passive: true });
    }
  } catch {
    /* older engines */
  }
}

// AT LOAD snapshot: captured at module evaluation time — this runs inside the
// initial module graph (main.jsx import), i.e. within the first ~100ms of page
// load, BEFORE React renders, so pre-hydration viewport values are recorded.
const atLoadSnapshot: VpSnapshot = VP_DEBUG_ENABLED && safeProbe
  ? collectSnapshot(safeProbe ? safeProbe.offsetHeight : 0)
  : ({} as VpSnapshot); // placeholder; never read while the chip is gated off

function fmtSnapshot(s: VpSnapshot): string[] {
  const L: string[] = [];
  L.push('t=' + new Date(s.at).toISOString().slice(11, 23));
  L.push('inner ' + s.innerWidth + 'x' + s.innerHeight);
  if (s.visualViewport) {
    L.push('vv h=' + s.visualViewport.height + ' offT=' + s.visualViewport.offsetTop + ' offL=' + s.visualViewport.offsetLeft + ' pageT=' + s.visualViewport.pageTop + ' scale=' + s.visualViewport.scale);
  } else {
    L.push('vv null');
  }
  if (s.documentElement) {
    L.push('de cl=' + s.documentElement.clientHeight + 'x' + s.documentElement.clientWidth + ' sh=' + s.documentElement.scrollHeight + ' h=' + s.documentElement.computedHeight + ' mh=' + s.documentElement.computedMinHeight);
  }
  if (s.body) {
    L.push('body rect t=' + s.body.top + ' b=' + s.body.bottom + ' h=' + s.body.height + ' css h=' + s.body.computedHeight + ' mh=' + s.body.computedMinHeight);
  }
  if (s.root) {
    L.push('#root t=' + s.root.top + ' b=' + s.root.bottom + ' h=' + s.root.height);
  }
  L.push('safe-inset top=' + s.safeAreaInsetTop + 'px bottom=' + s.safeAreaInsetBottom + 'px');
  L.push('resize-ev win=' + s.resizeEvents.windowResize + ' vv=' + s.resizeEvents.visualViewportResize);
  L.push(s.vvhDebug ? 'vvhFix h0=' + s.vvhDebug.h0 + ' h1=' + s.vvhDebug.h1 + ' pass=' + s.vvhDebug.pass + ' published=' + s.vvhDebug.published : 'vvhFix: none');
  L.push('strip@ih+30: ' + (s.stripProbe ? s.stripProbe.target + ' bg=' + s.stripProbe.bg : 'none') + ' de-bg=' + s.htmlBg);
  const one = (n: string, v: { top: number; bottom: number; height: number; computedHeight: string } | null) =>
    v ? n + ' t=' + v.top + ' b=' + v.bottom + ' h=' + v.height + ' css=' + v.computedHeight : n + ' absent';
  L.push(one('hdr', s.appHeader));
  L.push(one('nav', s.appNav));
  L.push(one('shb', s.appShellBottom));
  L.push('hit@bottom-2: ' + (s.probes.atCenterBottom ? s.probes.atCenterBottom.target + ' bg=' + s.probes.atCenterBottom.bg : 'none'));
  L.push('hit@bottom-safe-2: ' + (s.probes.aboveSafeArea ? s.probes.aboveSafeArea.target + ' bg=' + s.probes.aboveSafeArea.bg : 'none'));
  L.push('hit@top+2: ' + (s.probes.atTop ? s.probes.atTop.target + ' bg=' + s.probes.atTop.bg : 'none'));
  L.push('scrollY=' + s.scroll.windowScrollY + ' scroller=' + s.scroll.scroller + (s.scroll.scrollerClientHeight !== null ? ' st=' + s.scroll.scrollerScrollTop + ' ch=' + s.scroll.scrollerClientHeight + ' sh=' + s.scroll.scrollerScrollHeight : ''));
  L.push('standalone=' + s.displayModeStandalone + ' online=' + s.navigatorOnLine);
  return L;
}

const VpDebugChip: React.FC = () => {
  // Opt-in gate (see header): standalone AND vpdebug=1 this session. Fixed for
  // the component's lifetime; the early return below keeps gated-off renders
  // at null before any layout-affecting work (all effects no-op while !visible).
  const [visible] = useState<boolean>(() => VP_DEBUG_ENABLED);
  const [expanded, setExpanded] = useState(false);
  const [now, setNow] = useState<VpSnapshot | null>(null);
  const lastTapRef = useRef(0);
  const [copied, setCopied] = useState(false);
  const [fallbackText, setFallbackText] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    // eslint-disable-next-line no-console
    console.log('[vpdebug]', JSON.stringify({ atLoad: atLoadSnapshot }));
  }, [visible]);

  useEffect(() => {
    if (!expanded) return;
    const tick = () => setNow(collectSnapshot(safeProbe ? safeProbe.offsetHeight : 0));
    tick();
    const id = window.setInterval(tick, 500);
    return () => window.clearInterval(id);
  }, [expanded]);

  const dump = useCallback(() => {
    const json = JSON.stringify({ atLoad: atLoadSnapshot, now: now ?? collectSnapshot(safeProbe ? safeProbe.offsetHeight : 0) });
    // eslint-disable-next-line no-console
    console.log('[vpdebug]', json);
    const done = () => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(json).then(done).catch(() => setFallbackText(json));
    } else {
      setFallbackText(json);
    }
  }, [now]);

  const onChipTap = useCallback(() => {
    const t = Date.now();
    if (t - lastTapRef.current < 300) {
      lastTapRef.current = 0;
      dump();
      return;
    }
    lastTapRef.current = t;
    setExpanded((e) => {
      if (!e) {
        // eslint-disable-next-line no-console
        console.log('[vpdebug]', JSON.stringify({ atLoad: atLoadSnapshot }));
      }
      return !e;
    });
  }, [dump]);

  // Gated off: render null EARLY — no DOM, no listeners, no console output.
  if (!visible) return null;

  return (
    <>
      <button
        type="button"
        onClick={onChipTap}
        style={{
          position: 'fixed',
          bottom: 'calc(4rem + env(safe-area-inset-bottom) + 8px)',
          left: '8px',
          zIndex: 30,
          background: 'rgba(10,10,12,0.92)',
          color: '#7dd3fc',
          font: '600 10px/1 ui-monospace, SFMono-Regular, Menlo, monospace',
          padding: '6px 8px',
          borderRadius: '8px',
          border: '1px solid rgba(125,211,252,0.35)',
          pointerEvents: 'auto',
        }}
      >
        {'VD ' + window.innerHeight + 'px'}
      </button>
      {expanded && (
        <div
          style={{
            position: 'fixed',
            bottom: 'calc(4rem + env(safe-area-inset-bottom) + 44px)',
            left: '8px',
            width: '300px',
            maxHeight: '60vh',
            overflowY: 'auto',
            zIndex: 30,
            background: 'rgba(8,10,14,0.96)',
            color: '#a5f3fc',
            font: '11px/1.5 ui-monospace, SFMono-Regular, Menlo, monospace',
            padding: '10px',
            borderRadius: '10px',
            border: '1px solid rgba(125,211,252,0.35)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            pointerEvents: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ color: '#fff', marginBottom: 6 }}>AT LOAD</div>
          {fmtSnapshot(atLoadSnapshot).map((l, i) => (
            <div key={'a' + i}>{l}</div>
          ))}
          <div style={{ color: '#fff', margin: '8px 0 6px' }}>NOW</div>
          {now && fmtSnapshot(now).map((l, i) => <div key={'n' + i}>{l}</div>)}
          <button
            type="button"
            onClick={dump}
            style={{ marginTop: 8, font: '600 11px ui-monospace, monospace', color: '#0a0a0c', background: '#7dd3fc', border: 'none', padding: '6px 10px', borderRadius: 6 }}
          >
            {copied ? 'COPIED ✓' : 'COPY JSON'}
          </button>
        </div>
      )}
      {fallbackText && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 30,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => setFallbackText(null)}
        >
          <textarea
            readOnly
            value={fallbackText}
            style={{ width: '90%', height: '60%', font: '10px ui-monospace, monospace', background: '#0a0a0c', color: '#7dd3fc', border: '1px solid #7dd3fc' }}
            onFocus={(e) => e.currentTarget.select()}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default VpDebugChip;
